import { cn } from "@/lib/utils";
import { useEffect, useLayoutEffect, useState, useCallback, useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const STORAGE_KEY = "zensio-tour-completed-v1";

export interface TourStep {
  /** data-tour attribute value on the target element. Omit for centered modal. */
  target?: string;
  title: string;
  body: string;
  /** Route to navigate to before showing this step */
  route?: string;
  /** Preferred placement of the popover */
  placement?: "top" | "bottom" | "left" | "right" | "center";
}

const STEPS: TourStep[] = [
  {
    title: "Välkommen till Zensio 👋",
    body: "Låt oss ta en snabb rundtur genom de viktigaste funktionerna. Klicka Nästa så hoppar vi automatiskt till varje sektion.",
    placement: "center",
    route: "/dashboard",
  },
  {
    target: "page-title",
    title: "AI Action Center",
    body: "På Dashboard samlas dina viktigaste prioriteringar idag: heta leads, obesvarade ärenden, möten och utkast som väntar.",
    placement: "bottom",
    route: "/dashboard",
  },
  {
    target: "page-title",
    title: "Content Studio",
    body: "Här skapar AI:n inlägg åt dig — anpassade för varje plattform, med bilder och schemaläggning.",
    placement: "bottom",
    route: "/studio",
  },
  {
    target: "page-title",
    title: "Planerare",
    body: "Kalender över alla inlägg. Dra och släpp för att flytta, eller byt till agenda-vy.",
    placement: "bottom",
    route: "/planner",
  },
  {
    target: "page-title",
    title: "Analys",
    body: "Statistik, jämförelser och AI-rekommendationer baserat på ditt innehåll.",
    placement: "bottom",
    route: "/analytics",
  },
  {
    target: "page-title",
    title: "Marknadsföring",
    body: "Bygg kampanjer, nyhetsbrev och automationsflöden som körs själva.",
    placement: "bottom",
    route: "/marknadsforing",
  },
  {
    target: "page-title",
    title: "Försäljning",
    body: "Komplett CRM med AI-kvalificerade leads, offerter och mötesbokning — allt på ett ställe.",
    placement: "bottom",
    route: "/sales",
  },
  {
    target: "page-title",
    title: "Kundservice",
    body: "AI-inkorg som svarar på enkla ärenden automatiskt och föreslår svar på resten.",
    placement: "bottom",
    route: "/kundservice",
  },
  {
    target: "page-title",
    title: "Företagets minne",
    body: "Lär Zensio om ditt företag — sedan kan du fråga AI:n om vad som helst med kontext från din data.",
    placement: "bottom",
    route: "/minne",
  },
  {
    title: "Du är redo! 🚀",
    body: "Du kan starta rundturen igen när som helst från Hjälp-menyn (?-knappen). Lycka till med Zensio!",
    placement: "center",
    route: "/dashboard",
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function useTargetRect(target: string | undefined): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useLayoutEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }
    let raf: number;
    let attempts = 0;
    const measure = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
      if (!el) {
        attempts += 1;
        if (attempts > 60) {
          // Give up after ~1s and fall back to centered popover
          setRect(null);
          return;
        }
        raf = requestAnimationFrame(measure);
        return;
      }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      // Wait for scroll to settle
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }, 250);
    };
    // Reset before probing new target
    setRect(null);
    raf = requestAnimationFrame(measure);
    const onResize = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [target]);

  return rect;
}

function getPopoverStyle(rect: Rect | null, placement: TourStep["placement"]): React.CSSProperties {
  if (!rect || placement === "center") {
    // No transform here: the fade-in animation overrides inline transforms,
    // which would break centering. A flex wrapper handles centering instead.
    return {
      width: "min(340px, calc(100vw - 32px))",
      maxWidth: "calc(100vw - 32px)",
    };
  }
  const gap = 16;
  const vw = window.innerWidth;
  const popoverWidth = Math.min(340, vw - 32);
  const vh = window.innerHeight;
  // Phones: pin the card to the bottom of the screen so it's always reachable
  if (vw < 640) {
    return { left: 12, right: 12, bottom: 16, width: "auto", maxWidth: "none" };
  }


  let top = 0;
  let left = 0;
  let actual = placement ?? "bottom";

  // Auto-flip if not enough room
  if (actual === "bottom" && rect.top + rect.height + gap + 180 > vh) actual = "top";
  if (actual === "top" && rect.top - gap - 180 < 0) actual = "bottom";

  switch (actual) {
    case "top":
      top = Math.max(8, rect.top - gap - 180);
      left = rect.left + rect.width / 2 - popoverWidth / 2;
      break;
    case "right":
      top = rect.top + rect.height / 2 - 90;
      left = rect.left + rect.width + gap;
      break;
    case "left":
      top = rect.top + rect.height / 2 - 90;
      left = rect.left - gap - popoverWidth;
      break;
    case "bottom":
    default:
      top = rect.top + rect.height + gap;
      left = rect.left + rect.width / 2 - popoverWidth / 2;
      break;
  }
  // Clamp horizontally
  left = Math.max(8, Math.min(vw - popoverWidth - 8, left));
  return { top, left, width: popoverWidth, maxWidth: "calc(100vw - 16px)" };
}

export function ProductTour({
  open,
  onClose,
  startAt = 0,
}: {
  open: boolean;
  onClose: () => void;
  startAt?: number;
}) {
  const [index, setIndex] = useState(startAt);
  const navigate = useNavigate();
  const location = useLocation();
  const step = STEPS[index];
  const rect = useTargetRect(open ? step?.target : undefined);

  // Navigate to required route before measuring
  useEffect(() => {
    if (!open) return;
    if (step?.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [open, index, step, location.pathname, navigate]);

  const finish = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    onClose();
  }, [onClose]);

  const next = () => {
    if (index >= STEPS.length - 1) {
      finish();
    } else {
      setIndex((i) => i + 1);
    }
  };
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  if (!open || !step) return null;

  const popoverStyle = getPopoverStyle(rect, step.placement);
  const isCentered = !step.target || step.placement === "center" || !rect;

  return createPortal(
    <div className="fixed inset-0 z-[100] pointer-events-none" aria-live="polite">
      {/* Backdrop with cut-out via box-shadow */}
      {!isCentered && rect ? (
        <div
          className="absolute pointer-events-auto rounded-xl ring-4 ring-primary/70 transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.55)",
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div className="absolute inset-0 bg-black/55 pointer-events-auto" onClick={finish} />
      )}

      {/* Popover */}
      <div className={isCentered ? "absolute inset-0 flex items-center justify-center p-4 pointer-events-none" : "contents"}>
      <div
        className={cn(
          "pointer-events-auto rounded-xl border border-border bg-card text-card-foreground shadow-2xl p-5 animate-fade-in",
          isCentered ? "relative" : "absolute"
        )}
        style={popoverStyle}
      >
        <button
          onClick={finish}
          className="absolute right-2 top-2 p-1 rounded-md text-muted-foreground hover:bg-accent"
          aria-label="Stäng rundtur"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <div className="rounded-full bg-primary/10 p-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
          </div>
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Steg {index + 1} av {STEPS.length}
          </span>
        </div>

        <h3 className="text-base font-semibold text-foreground mb-1.5 pr-6">{step.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === index ? "w-5 bg-primary" : "w-1.5 bg-muted"
                }`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {index > 0 && (
              <Button variant="ghost" size="sm" onClick={prev} className="gap-1">
                <ArrowLeft className="h-3.5 w-3.5" />
                Tillbaka
              </Button>
            )}
            <Button size="sm" onClick={next} className="gap-1">
              {index >= STEPS.length - 1 ? "Klar" : "Nästa"}
              {index < STEPS.length - 1 && <ArrowRight className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </div>
      </div>
    </div>,
    document.body,
  );
}

// ---- Global tour store: survives page navigation between tour steps ----
let tourOpen = false;
const listeners = new Set<() => void>();
function setTourOpen(v: boolean) {
  tourOpen = v;
  listeners.forEach((l) => l());
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

function markSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
  // Persist per account so it never shows again, on any device
  supabase.auth.updateUser({ data: { product_tour_seen: true } }).catch(() => {});
}

export function useProductTour(autoStart: boolean = true) {
  const open = useSyncExternalStore(subscribe, () => tourOpen);
  const { user } = useAuth();

  useEffect(() => {
    if (!autoStart || !user) return;
    let done = false;
    try {
      done = !!localStorage.getItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if (done || user.user_metadata?.product_tour_seen) return;
    const t = setTimeout(() => {
      markSeen(); // first login only, even if the tour is closed early
      setTourOpen(true);
    }, 800);
    return () => clearTimeout(t);
  }, [autoStart, user]);

  return {
    open,
    start: () => setTourOpen(true),
    close: () => setTourOpen(false),
  };
}

/** Mounted once at app level so the tour keeps running across pages. */
export function ProductTourHost() {
  const open = useSyncExternalStore(subscribe, () => tourOpen);
  if (!open) return null;
  return <ProductTour open onClose={() => setTourOpen(false)} />;
}

export default ProductTour;